import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { StateService } from '@uirouter/core';

import { CimaCore, Cloudinary, Util } from 'core/services';
import { CmsVideoResolution } from 'store/cms/models';
import { LineSummary } from 'store/account-summary/models';
import { VideoResolutionInfo } from 'store/video-resolution/models';
import { DataPlanTypePlanEight, DataPlanTypePlanSeven, PlanSevenUnlPremiumPlanIds } from 'core/constants';
import { UnlPlanSubgroup, UserRoles } from 'my-account/constants';

@Component({
    selector: 'xm-video-resolution',
    styleUrls: ['./video-resolution.scss'],
    templateUrl: './video-resolution.html',
    standalone: false
})
export class VideoResolutionComponent implements OnInit, OnDestroy {
    @Input() public isLastChild?: boolean;
    @Input() public currentLine: LineSummary;
    @Input() public isSegmentFive: boolean;
    @Input() public isSegmentSix: boolean;
    @Input() public videoInfo: VideoResolutionInfo;
    @Input() public pageData: CmsVideoResolution;
    @Input() public userRole: string;

    public displayImage: MediaImageOptions;
    public videoDefinition: VideoResoultionDefinition;
    public description: string;
    public buttonCTA: string;
    public isBusinessUser: boolean;
    public shouldDisableButton: boolean = false;

    private state: StateService;
    private subscriptions: Subscription[] = [];
    private cimaCore: CimaCore;

    constructor(state: StateService, cimaCore: CimaCore) {
        Object.assign(this, { state, cimaCore });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;
        
        if (this.pageData) {
            this.displayImage = {
                mobile: Cloudinary.generateMediaFromCms(this.pageData.videoIcon)
            };
        }
        if (this.videoInfo && this.pageData) {
            this.videoDefinition = this.getVideoResolutionContent();
            this.description = this.getVideoResolutionDescription();
            this.buttonCTA = this.videoInfo.isPendingDenialState ? this.pageData.pendingDenialState.buttonCTA : this.videoDefinition.switchCTA;
        }

        this.shouldDisableButton = this.videoInfo.isPendingDenialState || this.userRole === UserRoles.MEMBER || this.userRole === UserRoles.VIEWER;
    }

    public switchOnResolution(): void { 
        this.state.go('account.activity.details.video-resolution.progress');
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    private getVideoResolutionContent(): VideoResoultionDefinition {
        if (this.isSegmentFive) {
            if (this.currentLine.isTablet) {
                return this.videoInfo.isHighDefinition ? this.pageData.highDefinitionTabletPlansSeven : this.pageData.standardDefinitionTabletPlansSeven;
            } else {
                return this.videoInfo.isHighDefinition ? this.pageData.highDefinitionPhonePlansSeven : this.pageData.standardDefinitionPhonePlansSeven;
            }
        } else if (this.isSegmentSix) {
            if (this.currentLine.isTablet) {
                return this.videoInfo.isHighDefinition ? this.pageData.highDefinitionTabletPlansSeven : this.pageData.standardDefinitionTabletPlansSeven;
            } else {
                if (this.currentLine.planSubgroup === UnlPlanSubgroup.PLUS) {
                    return this.videoInfo.isHighDefinition ? this.pageData.hdMobilePlusPlansEight : this.pageData.sdMobilePlusPlansEight;
                } else {
                    return this.videoInfo.isHighDefinition ? this.pageData.hdMobileSelectPlansEight : this.pageData.sdMobileSelectPlansEight;
                }
            }
        }

        return this.videoInfo.isHighDefinition ? this.pageData.highDefinition : this.pageData.standardDefinition;
    }

    private getVideoResolutionDescription(): string {
        if (this.isSegmentFive) {
            if (this.currentLine.isTablet) {
                if (!this.isBusinessUser) {
                    return this.replacePlanName(DataPlanTypePlanSeven.TABLET_UNLIMITED);
                }

                return this.currentLine.planSubgroup === UnlPlanSubgroup.BASIC ? this.replacePlanName(DataPlanTypePlanSeven.TABLET_UNLIMITED_INTRO) 
                    : this.replacePlanName(DataPlanTypePlanSeven.TABLET_UNLIMITED_PREMIUM);
            } else {
                if (this.currentLine.planSubgroup === UnlPlanSubgroup.PREMIUM) {
                    const isPlansSevenUnlPremium: boolean = PlanSevenUnlPremiumPlanIds.includes(this.currentLine.planId);
                    if (this.isBusinessUser) {
                        return isPlansSevenUnlPremium ? this.replacePlanName(DataPlanTypePlanSeven.UNLIMITED_PREMIUM_FLEX)
                            : this.replacePlanName(DataPlanTypePlanSeven.UNLIMITED_PREMIUM);
                    } else {
                        return isPlansSevenUnlPremium ? this.replacePlanName(DataPlanTypePlanSeven.PREMIUM_UNLIMITED)
                            : this.replacePlanName(DataPlanTypePlanSeven.UNLIMITED_PLUS);
                    }
                } else {
                    return this.replacePlanName(DataPlanTypePlanSeven.UNLIMITED);
                }
            }
        } else if (this.isSegmentSix) {
            if (this.currentLine.isTablet) {
                return this.replacePlanName(DataPlanTypePlanSeven.TABLET_UNLIMITED);

            } else {
                if (this.currentLine.planSubgroup === UnlPlanSubgroup.PLUS) {
                    return this.replacePlanName(DataPlanTypePlanEight.MOBILE_PLUS);
                } else {
                    return this.replacePlanName(DataPlanTypePlanEight.MOBILE_SELECT);
                }
            }
        } else {
            return this.videoDefinition.description.replace('[PLAN_NAME]', this.planSubGroupName(this.currentLine.planSubgroup));
        }
    }

    private replacePlanName(planName: string): string {
        return this.videoDefinition.description.replace('[PLAN_NAME]', planName);
    }

    private planSubGroupName(value: string): string {
        return value && value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    }
}
